import { Alert, Stack } from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ExtractionReviewDialog, ExtractionReviewGrid } from "components/Document/ExtractionReview";
import { useWarnBeforeUnload } from "hooks/useWarnBeforeUnload";
import { useCallback, useMemo, useState } from "react";

import { BlockingIssues } from "./BlockingIssues";
import {
  toExtractionResult,
  toRecordValues,
  WASTE_PROFILE_REVIEW_SCHEMA,
} from "./extractionReview";
import { WasteStreamProfileData } from "./types";
import { useApplyReviewedProfile } from "./useApplyReviewedProfile";
import { useProfileReading } from "./useProfileReading";

export const AutoFillProfileDialog = ({
  open,
  onClose,
  wasteStream,
  documentId,
  documentTitle,
}: {
  open: boolean;
  onClose: () => void;
  /** The stream as it stands, so the grid can show what the sheet does not say. */
  wasteStream: WasteStreamProfileData;
  documentId: string;
  documentTitle: string;
}) => {
  // Reads the document on open when nothing has read it yet — see
  // `useProfileReading`. There is no prompt to read it, so no state here needs to
  // offer one.
  const { reading, loading, status, startError, requestReading } = useProfileReading(documentId);

  const { validate, confirm, applying, blockingIssues, refusals } = useApplyReviewedProfile({
    wasteStreamId: wasteStream.id,
    documentId,
    onApplied: onClose,
  });

  const extraction = useMemo(
    () => (reading ? toExtractionResult(reading, wasteStream) : null),
    [reading, wasteStream]
  );

  // What applying this reading would write over. The grid flags the divergence
  // rather than deciding it, so a hand-entered value or a constituent the sheet
  // does not list is the reviewer's call instead of a silent replacement.
  const record = useMemo(() => toRecordValues(wasteStream), [wasteStream]);

  // Nothing here is saved until the reviewer applies, and a dismissed warning is
  // not saved at all — it lives as long as the dialog does. Mid-apply a close
  // refuses outright: the write is in flight, and closing would leave the
  // reviewer unsure whether it landed.
  //
  // Two reasons to ask before closing, and the second is the one that used to be
  // missed. `hasWork` is something the reviewer did. `pendingCount` is values
  // this reading would write to the profile — which includes every value they
  // agreed with by doing nothing, since agreeing takes no keystroke. Guarding
  // only on the first meant a reviewer who read the sheet, judged it right and
  // closed lost the whole reading with nothing said.
  const [work, setWork] = useState({ hasWork: false, pendingCount: 0 });
  const [confirmingClose, setConfirmingClose] = useState(false);
  // Same guard, same reason, and the same two signals the close prompt uses: a
  // refresh loses an unapplied reading exactly as a close does.
  useWarnBeforeUnload(work.hasWork || work.pendingCount > 0);

  const requestClose = useCallback(() => {
    if (applying) return;
    if (work.hasWork || work.pendingCount > 0) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  }, [applying, work, onClose]);

  return (
    <ExtractionReviewDialog
      open={open}
      onClose={requestClose}
      // Matches the document-intelligence ingestion flow, which the reviewer may
      // well have arrived from: same activity, same name.
      title="Review & Confirm Details"
      document={wasteStream.profile?.profileDocument}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {blockingIssues.length > 0 && (
          <BlockingIssues
            key={refusals}
            title="Fix these before applying"
            messages={blockingIssues}
          />
        )}

        <ExtractionReviewGrid
          schema={WASTE_PROFILE_REVIEW_SCHEMA}
          extraction={extraction}
          record={record}
          status={status}
          loading={loading}
          extractionError={startError ?? reading?.extractionError}
          onCommit={confirm}
          onCancel={requestClose}
          onRetry={requestReading}
          validator={validate}
          committing={applying}
          onWorkInProgress={setWork}
          // A refused Apply focuses BlockingIssues, the list of reasons.
          shouldFocusError={false}
          confirmLabel="Apply to profile"
          description={`Read from ${documentTitle}. Review each value before applying it — everything here is editable.`}
        />

        {!loading && reading == null && (
          <Alert severity="info">This document is no longer available to read.</Alert>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmingClose}
        onClose={() => setConfirmingClose(false)}
        onConfirm={onClose}
        header="Close without applying?"
        // Worded from what is actually at stake. The count is values this reading
        // would change on the profile, so it covers the reviewer who agreed with
        // the sheet and never typed — the case the old wording ("values you have
        // edited") told nothing.
        info={
          work.pendingCount > 0
            ? `${work.pendingCount === 1 ? "1 value" : `${work.pendingCount} values`} from this reading ${
                work.pendingCount === 1 ? "differs" : "differ"
              } from the profile and ${work.pendingCount === 1 ? "is" : "are"} not saved until you apply. Closing now discards this reading.`
            : "The warnings you have dismissed are not saved until you apply this reading. Closing now discards them."
        }
        confirmText="Yes, Discard & Close"
      />
    </ExtractionReviewDialog>
  );
};
